import type { FC } from 'react'
import React, { memo, useEffect, useState } from 'react'
import type { PublicationDataDtoV1 } from '@/api/kubik/infoportal'
import { GetClientPublicationsQueryParamsType } from '@/api/kubik/infoportal'
import { Articles } from '@/components/resolvers'
import { ResponsiveBlocksPerGrid } from '@/components/resolvers/Articles/ResponsiveBlocksPerGrid'
import { PaginationMoreButton } from '@/components/ui'
import { EJournalClickEventBlockId, JournalGtmClickEventPayload } from '@/pages/Journal/gtmConstants'
import { openArticleClicked } from '@/pages/Journal/gtmHelpers'
import { useResponsiveValue } from '@/utils/hooks/responsive'
import { useInfiniteJournalPublications } from '@/utils/infoportal/hooks/useInfiniteJournalPublications'
import { useJournalUrlParams } from '@/utils/infoportal/hooks/useJournalUrlParams'
import type { IFilteredAllArticlesProps } from './types'
import './styles.scss'

const FilteredAllArticles: FC<IFilteredAllArticlesProps> = props => {
  const { entriesByTag, className } = props
  const { tagId } = useJournalUrlParams()
  const [publicationsDiff, setPublicationsDiff] = useState(0)
  const [filteredArticles, setFilteredArticles] = useState<PublicationDataDtoV1[]>(entriesByTag)

  const itemsPerPage = useResponsiveValue(ResponsiveBlocksPerGrid)

  const { hasNextPage, getNextPage, publications, isPublicationsLoading, isPublicationsInitialLoading } = useInfiniteJournalPublications({
    type: GetClientPublicationsQueryParamsType.ARTICLE,
    filteredDiff: publicationsDiff,
    extraKey: tagId ? `tagsPublications-tag${tagId}` : 'filteredArticles',
  })

  useEffect(() => {
    const filteredItems = publications?.filter(publication => !entriesByTag.some(entry => entry.id === publication.id))

    if (!filteredItems) return

    setFilteredArticles(filteredItems)

    if (filteredItems.length % 13 === 0) {
      setPublicationsDiff(0)

      return
    }

    if (hasNextPage) {
      setPublicationsDiff(itemsPerPage - (filteredItems.length % 13))
    }
  }, [publications, entriesByTag, itemsPerPage, hasNextPage])

  return (
    <div className={className}>
      <Articles
        className="filteredArticles__articles"
        articles={filteredArticles}
        isArticlesLoading={isPublicationsInitialLoading}
        blockId={EJournalClickEventBlockId.listArticles}
        onClick={openArticleClicked(JournalGtmClickEventPayload.listArticles)}
      />

      <div className="filteredArticles__paginationContainer">
        <PaginationMoreButton
          className="journalByTags__paginationMore"
          isLoading={isPublicationsLoading}
          onClick={getNextPage}
          hasNextPage={hasNextPage}
        />
      </div>
    </div>
  )
}

export default memo(FilteredAllArticles)
